import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { DocumentState } from "@/services/backend/documents/types/state";
import type { WaterKind } from "@/services/backend/samples/types/water-sample";
import { backendApi } from "@/services/backend/api";
import {
  LabData,
  Report,
  VDI6022,
} from "@/services/backend/htz/report/types/report";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { Address } from "@/services/backend/addresses/types/address";
import { Result } from "@/services/backend/htz/inspection/components/inspection-result";
import { Kind } from "@/services/backend/samples/types/kind";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { UUID } from "@bitory-gmbh/nidavellir";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const htzReportApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["HtzReport"],
  })
  .injectEndpoints({
    // The items of a summary mirror values of the report they summarize,
    // and the backend refreshes them whenever a report changes. Every
    // report mutation therefore drops the summary caches as well.
    endpoints: (build) => ({
      htzReportCreate: build.mutation<Report, CreateRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      htzReportShow: build.query<Report, ShowRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzReport"),
      }),
      htzReportPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: request,
          headers: {
            Accept: "application/pdf",
          },
          responseHandler: objectURLResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzReportPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: "/htz/report",
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzReportList: build.query<Lst<Report>, ListRequest>({
        query: (request) => ({
          url: "/htz/report/list",
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) => providesList(result?.items, "HtzReport"),
      }),
      htzReportPatch: build.mutation<Report, PatchRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportResetReferenceNumber: build.mutation<
        Report,
        ResetReferenceNumberRequest
      >({
        query: (request) => ({
          url: `/htz/report/reset-reference-number`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportUpdateState: build.mutation<Report, UpdateStateRequest>({
        query: (request) => ({
          url: "/htz/report/state",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportCreateItem: build.mutation<Report, CreateItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportPatchItem: build.mutation<Report, PatchItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportRemoveItem: build.mutation<Report, RemoveItemRequest>({
        query: (request) => ({
          url: "/htz/report/item",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportAddAssessment: build.mutation<Report, AddAssessmentRequest>({
        query: (request) => ({
          url: "/htz/report/assessment",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      /**
       * Adds a microbial assessment and creates the sample it references,
       * in one transaction: the backend creates the sample with the
       * defaults of the company's sample base data. Either both exist
       * afterwards or neither does.
       */
      htzReportAddMicrobialAssessmentWithSample: build.mutation<
        Report,
        AddMicrobialAssessmentWithSampleRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/microbial",
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
          "SamplesSampleViewModel",
        ],
      }),
      htzReportRemoveAssessment: build.mutation<
        Report,
        RemoveAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportUpdateAssessmentResult: build.mutation<
        Report,
        UpdateAssessmentResultRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment-result",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportPatchMicrobialAssessment: build.mutation<
        Report,
        PatchMicrobialAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/microbial",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportUpdateMicrobialAssessmentSample: build.mutation<
        Report,
        UpdateMicrobialAssessmentSampleRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/microbial/sample",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportPatchPhoto: build.mutation<Report, PatchPhotoRequest>({
        query: (request) => ({
          url: "/htz/report/assessment/photo",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportPatchStructuralAssessment: build.mutation<
        Report,
        PatchStructuralAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/structural",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportPatchVisualAssessment: build.mutation<
        Report,
        PatchVisualAssessmentRequest
      >({
        query: (request) => ({
          url: "/htz/report/assessment/visual",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
      htzReportDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: "/htz/report",
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          "HtzReport",
        ],
      }),
      // Assigning an air handling unit creates the ahu data owned by the
      // report, so the ahu data queries are invalidated as well.
      htzReportAssignAhu: build.mutation<Report, AssignAhuRequest>({
        query: (request) => ({
          url: "/htz/report/ahu",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzAirHandlingUnitData",
          "HtzSummary",
        ],
      }),
      // Rebuilding the items changes the report only, the air handling
      // unit data it reads the components from stays untouched.
      htzReportRebuildItemsFromAhu: build.mutation<
        Report,
        RebuildItemsFromAhuRequest
      >({
        query: (request) => ({
          url: "/htz/report/items-from-ahu",
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzReport", id: request.id },
          { type: "HtzReport", id: "LIST" },
          "HtzSummary",
        ],
      }),
    }),
  });

export const {
  useHtzReportCreateMutation,
  useHtzReportShowQuery,
  useHtzReportResetReferenceNumberMutation,
  useLazyHtzReportPdfPreviewQuery,
  useHtzReportPdfDownloadMutation,
  useHtzReportListQuery,
  useHtzReportPatchMutation,
  useHtzReportUpdateStateMutation,
  useHtzReportCreateItemMutation,
  useHtzReportPatchItemMutation,
  useHtzReportRemoveItemMutation,
  useHtzReportAddAssessmentMutation,
  useHtzReportAddMicrobialAssessmentWithSampleMutation,
  useHtzReportRemoveAssessmentMutation,
  useHtzReportUpdateAssessmentResultMutation,
  useHtzReportPatchMicrobialAssessmentMutation,
  useHtzReportUpdateMicrobialAssessmentSampleMutation,
  useHtzReportPatchPhotoMutation,
  useHtzReportPatchStructuralAssessmentMutation,
  useHtzReportPatchVisualAssessmentMutation,
  useHtzReportDeleteMutation,
  useHtzReportAssignAhuMutation,
  useHtzReportRebuildItemsFromAhuMutation,
} = htzReportApi;

// AssignAhuRequest assigns the air handling unit `ahuId` to the report `id`.
// The backend refuses reports that already have an air handling unit and
// air handling units of another customer or service object.
export interface AssignAhuRequest {
  id: string;
  ahuId: UUID;
}

// RebuildItemsFromAhuRequest replaces the items of the report `id` with one
// item per component of the air handling unit data the report is based on.
// The items are recreated and therefore get new ids. The assessments of the
// report are kept and the results of the new items are derived from them.
export interface RebuildItemsFromAhuRequest {
  id: string;
}

export interface CreateRequest {
  id: string;
  processId: UUID;
  companyId: UUID;
  branchId: UUID;
  customerId: UUID;
  serviceObjectId: UUID;
}

export interface ShowRequest {
  id: string;
}

export interface PDFRequest {
  reportId: string;
  document: ReportDocument;
  download?: boolean;
}

export enum ReportDocument {
  ALL = "all",
  REPORT = "report",
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  reportDate?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

// PatchRequest updates the report. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged (the backend
// `patch.Field` is "unset" when the JSON key is absent).
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
  date?: string | null;
  createdBy?: string;
  createdByCompany?: Address;
  objectAddress?: Address;
  createdFor?: Address;
  laboratories?: LabData[];
  inspectionDate?: string | null;
  inspectionConductedBy?: string;
  inspectionConductedByStaff?: string;
  lastInspectionDate?: string | null;
  nextInspectionDate?: string | null;
  vdi6022Judgement?: VDI6022;
  ahuImageFileId?: string;
}

export interface CreateItemRequest {
  id: string;
  itemId: string;
  order: number;
  componentId: string;
}

// PatchItemRequest updates an item. Only `order` is patchable: the
// results and recommendations of an item are always derived from the
// assessments of the report. An omitted key leaves the value unchanged.
export interface PatchItemRequest {
  id: string;
  itemId: string;
  order?: number;
}

export interface RemoveItemRequest {
  id: string;
  itemId: string;
}

export type AssessmentType = "microbial" | "photo" | "structural" | "visual";

/**
 * Adds a microbial assessment for the component together with a new sample
 * of the kind. `waterKind` is required for water samples only. The number
 * is generated by the client, see `generateSampleNumber`.
 */
export interface AddMicrobialAssessmentWithSampleRequest {
  id: string;
  assessmentId: string;
  componentId: string;
  sampleId: string;
  sampleKind: Kind;
  waterKind?: WaterKind;
  number: string;
  location: string;
  spot: string;
}

export interface AddAssessmentRequest {
  id: string;
  type: AssessmentType;
  assessmentId: string;
  componentId: string;
  // structural prefills the new assessment, e.g. from a catalog entry.
  // Only the patch matching type is applied by the backend.
  structural?: {
    requirement?: string;
    deficiency?: string;
    recommendation?: string;
    comment?: string;
    conductedAt?: string;
    result?: Result;
  };
  // visual prefills the new assessment, e.g. from a catalog entry. Only
  // the patch matching type is applied by the backend.
  visual?: {
    deficiency?: string;
    recommendation?: string;
    comment?: string;
    conductedAt?: string;
    result?: Result;
  };
}

export interface RemoveAssessmentRequest {
  id: string;
  assessmentId: string;
}

export interface UpdateAssessmentResultRequest {
  id: string;
  assessmentId: string;
  result: Result;
}

// PatchMicrobialAssessmentRequest patches a microbial assessment. Patch
// fields are optional; an omitted key leaves the value unchanged.
export interface PatchMicrobialAssessmentRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  conductedAt?: string;
  result?: Result;
  /** Overrides the recommendation the backend derives from the result. */
  recommendation?: string;
}

// UpdateMicrobialAssessmentSampleRequest links a sample to a microbial
// assessment. A zero kind clears the link, so the kind and the id are
// widened accordingly - the backend unlinks on a zero kind.
export interface UpdateMicrobialAssessmentSampleRequest {
  id: string;
  assessmentId: string;
  sampleKind: Kind | "";
  sampleId: string | null;
}

export interface PatchPhotoRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  comment?: string;
  // An explicit null removes the image of the photo. The file itself is
  // owned by the report and deleted by the client which stored it.
  fileId?: string | null;
  conductedAt?: string;
}

export type PatchStructuralAssessmentRequest = {
  id: string;
  assessmentId: string;
  componentId?: string;
  requirement?: string;
  deficiency?: string;
  recommendation?: string;
  comment?: string;
  conductedAt?: string;
  result?: Result;
};

export interface PatchVisualAssessmentRequest {
  id: string;
  assessmentId: string;
  componentId?: string;
  deficiency?: string;
  recommendation?: string;
  comment?: string;
  conductedAt?: string;
  result?: Result;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface DeleteRequest {
  id: string;
}

/**
 * Patches the cached report for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateHtzReportCache = updateCache(htzReportApi, "htzReportShow");
